import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardBody, CardHeader, Field, Input, Modal, ModalFooter, StatusPill } from '~/components/ui';
import { supabase } from '~/lib/supabase';
import { useAuth } from '~/lib/auth';
import type { Database } from '~/types/database';
import { adminCreateUser, adminDeactivateUser, adminUpdateUserRoles, adminUpdateUserPhone } from '~/lib/api/admin';
import { formatDateTime } from '~/lib/format';
import { passwordError, MIN_PASSWORD_LENGTH } from '~/lib/password';
import { STAFF_ROLES } from '~/lib/staffRoles';
import { RoleMockup } from '~/components/admin/RoleMockup';

type Profile = Database['public']['Tables']['profiles']['Row'];

export const Route = createFileRoute('/_app/admin/users')({
  component: UsersAdminPage,
});

function UsersAdminPage() {
  const qc = useQueryClient();
  const auth = useAuth();
  const nav = useNavigate();
  const [creating, setCreating] = useState(false);
  const [editingRoles, setEditingRoles] = useState<{ user_id: string; current: string[] } | null>(null);
  // Index into STAFF_ROLES of the role whose phone mockup is open (null = closed).
  const [previewRole, setPreviewRole] = useState<number | null>(null);

  const { data: users, isLoading } = useQuery({
    queryKey: ['admin_profiles'],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('user_id, email, display_name, last_name, username, phone, staff_role_keys, allowed_tabs, is_active, created_at, updated_at')
        .order('display_name');
      return data ?? [];
    },
  });

  function viewAs(u: NonNullable<typeof users>[number]) {
    // Preview the app exactly as this user sees it (UI/permissions only — your
    // own data access is unchanged). Replaces the old shared admin passcode.
    auth.setPreviewProfile(u as unknown as Profile);
    nav({ to: '/' });
  }

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data } = await supabase.from('roles').select('*').eq('is_active', true).order('sort_order');
      return data ?? [];
    },
  });

  const deactivate = useMutation({
    mutationFn: adminDeactivateUser,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin_profiles'] }),
  });

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--color-navy)]">Staff & Roles</h1>
          <p className="text-sm text-[var(--color-ink-soft)] mt-1">
            Add staff, assign roles, deactivate accounts. Roles control which compliance tasks land in their queue.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>+ Invite user</Button>
      </div>

      {/* The role taxonomy, above the user list — what each role can see,
          use, or edit, split app vs CRM (Tim, 7/20). */}
      <Card className="mt-6">
        <CardHeader>Roles — what each one can see &amp; do</CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {STAFF_ROLES.map((r, i) => (
              <button key={r.key} type="button" onClick={() => setPreviewRole(i)}
                className="rounded-lg border border-[var(--color-line)] p-3 text-left cursor-pointer transition-colors hover:border-[var(--color-navy-soft)] hover:bg-[var(--color-surface-1)]">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-[var(--color-navy)]">{r.label}</span>
                  <span className="text-xs font-mono text-[var(--color-ink-muted)]">{r.key}</span>
                  <span className="ml-auto text-[10px] font-semibold text-[var(--color-link-on-white)] whitespace-nowrap">
                    tap to preview their app ›
                  </span>
                </div>
                <div className="mt-1.5 text-xs leading-relaxed">
                  <span className="font-semibold uppercase tracking-wide text-[10px] text-[var(--color-ink-soft)]">App · </span>
                  {r.app}
                </div>
                <div className="mt-1 text-xs leading-relaxed">
                  <span className="font-semibold uppercase tracking-wide text-[10px] text-[var(--color-ink-soft)]">CRM · </span>
                  {r.crm}
                </div>
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-[var(--color-ink-muted)]">
            Office Support was merged into Office Admin; Project Manager was merged into Supervisor; Estimator is the role
            previously labeled Invoicing. In the app, every role's Jobs tab shows every job — search by job number,
            customer name, or property address.
          </p>
        </CardBody>
      </Card>

      <Card className="mt-6">
        <CardHeader>{(users ?? []).length} user(s)</CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="px-5 py-4 text-sm text-[var(--color-ink-muted)]">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface-1)] text-xs uppercase tracking-wide text-[var(--color-ink-soft)]">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2" title="Number RingCentral rings first on Dispatch → Call Now">Call-back #</th>
                  <th className="text-left px-4 py-2">Roles</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Created</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(users ?? []).map((u) => (
                  <tr key={u.user_id} className="border-t border-[var(--color-line)]">
                    <td className="px-4 py-2 font-semibold">{u.display_name}</td>
                    <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-2"><PhoneCell user_id={u.user_id} value={(u as { phone?: string | null }).phone ?? ''} /></td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(u.staff_role_keys ?? []).map((r: string) => (
                          <span key={r} className="px-2 py-0.5 rounded-full text-xs font-mono bg-[var(--color-blue-tint)] text-[var(--color-blue-deep)]">
                            {r}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {u.is_active
                        ? <StatusPill kind="completed">Active</StatusPill>
                        : <StatusPill kind="muted">Disabled</StatusPill>}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatDateTime(u.created_at)}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {u.is_active && u.user_id !== auth.realProfile?.user_id && (
                        <Button size="sm" variant="ghost" className="mr-1"
                          onClick={() => viewAs(u)}>
                          View as
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="mr-1"
                        onClick={() => setEditingRoles({ user_id: u.user_id, current: u.staff_role_keys ?? [] })}>
                        Roles
                      </Button>
                      {u.is_active && (
                        <Button size="sm" variant="ghost"
                          onClick={() => {
                            if (confirm(`Deactivate ${u.display_name}?`)) deactivate.mutate(u.user_id);
                          }}>
                          Disable
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {creating && <CreateUserModal onClose={() => setCreating(false)} />}
      {previewRole !== null && (
        <RoleMockupModal index={previewRole} onIndex={setPreviewRole} onClose={() => setPreviewRole(null)} />
      )}
      {editingRoles && (
        <EditRolesModal
          user_id={editingRoles.user_id}
          current={editingRoles.current}
          // Only the staff-role taxonomy is assignable (plus admin). The other
          // rows in `roles` are job-participant roles, not staff roles.
          allRoles={[
            ...STAFF_ROLES.map((r) => ({ key: r.key, label: r.label })),
            ...(roles ?? [])
              .filter((r) => r.role_key === 'admin')
              .map((r) => ({ key: r.role_key, label: r.label })),
          ]}
          onClose={() => setEditingRoles(null)}
        />
      )}
    </div>
  );
}

/**
 * Phone-mockup modal for a role card — a rough diagram of that role's mobile
 * app (bottom bar + Settings), with ‹ Prev / Next › to flip through the roles
 * in catalog order.
 */
function RoleMockupModal({ index, onIndex, onClose }: {
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const role = STAFF_ROLES[index];
  const step = (delta: number) =>
    onIndex((index + delta + STAFF_ROLES.length) % STAFF_ROLES.length);

  return (
    <Modal open onClose={onClose} title={`${role.label} — their app, roughly`} size="md">
      <div className="px-6 py-4">
        <RoleMockup role={role} />
      </div>
      <ModalFooter className="items-center">
        <span className="mr-auto text-xs text-[var(--color-ink-muted)]">
          Role {index + 1} of {STAFF_ROLES.length}
        </span>
        <Button type="button" variant="ghost" onClick={() => step(-1)}>‹ Prev</Button>
        <Button type="button" variant="ghost" onClick={() => step(1)}>Next ›</Button>
        <Button type="button" variant="primary" onClick={onClose}>Done</Button>
      </ModalFooter>
    </Modal>
  );
}

function PhoneCell({ user_id, value }: { user_id: string; value: string }) {
  const qc = useQueryClient();
  const [v, setV] = useState(value);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () => adminUpdateUserPhone(user_id, v),
    onSuccess: () => {
      setSaved(true); setTimeout(() => setSaved(false), 1200);
      qc.invalidateQueries({ queryKey: ['admin_profiles'] });
    },
  });
  return (
    <span className="inline-flex items-center gap-1">
      <Input
        value={v}
        placeholder="(none)"
        className="w-[150px] font-mono text-xs"
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (v.trim() !== (value ?? '').trim()) save.mutate(); }}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      />
      {saved && <span className="text-[10px] font-semibold text-[var(--color-green)]">✓</span>}
    </span>
  );
}

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ email: '', password: '', display_name: '', last_name: '' });
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => {
      const pwErr = passwordError(form.password);
      if (pwErr) throw new Error(pwErr);
      return adminCreateUser(form);
    },
    onSuccess: (r) => {
      if (!r.ok) { setError(r.error ?? 'unknown'); return; }
      qc.invalidateQueries({ queryKey: ['admin_profiles'] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal open onClose={onClose} title="Invite a new user" size="sm">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <div className="px-6 py-4 space-y-3">
          <Field label="Display Name" htmlFor="u_dn">
            <Input id="u_dn" required value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          </Field>
          <Field label="Last Name" htmlFor="u_ln">
            <Input id="u_ln" value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </Field>
          <Field label="Email" htmlFor="u_em">
            <Input id="u_em" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Initial password" htmlFor="u_pw"
            hint={`At least ${MIN_PASSWORD_LENGTH} characters; mix of cases, numbers, symbols. They can change it after signing in.`}>
            <Input id="u_pw" type="text" required minLength={MIN_PASSWORD_LENGTH} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          {error && <div className="text-sm text-[var(--color-danger)]">{error}</div>}
        </div>
        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create user'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function EditRolesModal({ user_id, current, allRoles, onClose }: {
  user_id: string;
  current: string[];
  allRoles: { key: string; label: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>(current);
  const save = useMutation({
    mutationFn: () => adminUpdateUserRoles(user_id, selected),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin_profiles'] }); onClose(); },
  });

  return (
    <Modal open onClose={onClose} title="Edit roles" size="sm">
      <div className="px-6 py-4 grid grid-cols-2 gap-2">
        {allRoles.map((r) => (
          <label key={r.key} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={selected.includes(r.key)}
              onChange={(e) => setSelected((cur) => e.target.checked
                ? [...cur, r.key]
                : cur.filter((k) => k !== r.key))} />
            {r.label} <span className="text-xs text-[var(--color-ink-muted)] font-mono">({r.key})</span>
          </label>
        ))}
      </div>
      <ModalFooter>
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="button" variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save roles'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
